<template>
  <v-card class="fill-height" rounded="lg">
    <v-card-title v-if="title" class="text-subtitle-1 pb-1">{{ title }}</v-card-title>
    <v-card-subtitle v-if="subtitle" class="pb-1">{{ subtitle }}</v-card-subtitle>
    <v-card-text>
      <!-- Interativo (dados JSON gravados pelo NGSolve): valores ao passar o mouse, zoom e rotação -->
      <Plotly
        v-if="plot"
        :data="plot.data"
        :layout="plot.layout"
        :display-mode-bar="!$vuetify.breakpoint.xsOnly"
        :displaylogo="false"
        :responsive="true"
        class="interactive-plot"
      />
      <!-- Simulações antigas só têm a imagem PNG -->
      <v-img v-else-if="imageUrl" :src="imageUrl" contain class="simulation-image" />
      <div v-else-if="error" class="text-body-2">{{ error }}</div>
      <div v-else class="d-flex justify-center py-8">
        <v-progress-circular color="primary" indeterminate />
      </div>
      <p v-if="caption" class="text-caption mt-2 mb-0">{{ caption }}</p>
      <p v-if="plot" class="text-caption mb-0">
        {{ simulationType === "3d"
          ? "Drag to rotate, scroll or pinch to zoom, hover (or tap) an arrow or a surface to see its values."
          : "Hover (or tap) a point to see x, y, the field and ε. Drag to zoom, double-click to reset." }}
      </p>
    </v-card-text>
  </v-card>
</template>
<script>
import { Plotly } from "vue-plotly";
import ngsolve from "@/services/ngsolve";

// O Plotly 1.58 não tem "Inferno" entre as escalas com nome: mesma escala do PNG do matplotlib
const INFERNO = [
  [0, "#000004"], [0.13, "#1b0c41"], [0.25, "#4a0c6b"], [0.38, "#781c6d"], [0.5, "#a52c60"],
  [0.63, "#cf4446"], [0.75, "#ed6925"], [0.88, "#fb9b06"], [1, "#fcffa4"]
];
const REGION_COLORS = ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"];
// "+ 0" e o arredondamento evitam exibir "-0.00"
const format = (value, digits = 3) => (Number(Number(value).toFixed(digits)) + 0).toFixed(digits);

export default {
  name: "Details3DChartCard",
  components: { Plotly },
  props: {
    simulationType: { type: String, default: "2d" },
    title: { type: String, default: "" },
    caption: { type: String, default: "" },
    // Geometrias da produção: a região "geo<N>" da malha é a N-ésima geometria (nome e ε no hover)
    geometries: { type: Array, default: () => [] }
  },
  data() {
    return {
      imageUrl: null,
      plot: null,
      subtitle: "",
      error: ""
    };
  },
  methods: {
    regionInfo(region) {
      const index = Number(String(region).replace("geo", "")) - 1;
      const geometry = this.geometries[index] || {};
      return {
        name: geometry.name || region,
        epsilon: Number(geometry.epsilon) || null,
        color: REGION_COLORS[index % REGION_COLORS.length] || REGION_COLORS[0]
      };
    },
    // Corte: |Re(E)| no plano z, contorno das geometrias (onde ε muda) e as fontes
    cutPlot(content) {
      const levels = [...new Set(content.epsilon.flat().filter(v => v != null))].sort((a, b) => a - b);
      const contours = levels.slice(1).map(level => ({
        type: "contour",
        x: content.x,
        y: content.y,
        z: content.epsilon,
        contours: { start: level - 1e-6, end: level - 1e-6, size: 1, coloring: "lines" },
        colorscale: [[0, "#00e5ff"], [1, "#00e5ff"]],
        line: { width: 1.5 },
        showscale: false,
        hoverinfo: "skip"
      }));
      const sources = {
        type: "scatter",
        mode: "markers",
        x: content.sources.map(s => s[0]),
        y: content.sources.map(s => s[1]),
        marker: { symbol: "star", size: 14, color: "#00e5ff", line: { color: "#000", width: 1 } },
        text: content.sources.map((s, i) => `Source ${i + 1}<br>(${format(s[0])}, ${format(s[1])}, ${format(s[2])}) µm`),
        hovertemplate: "%{text}<extra></extra>"
      };
      return {
        data: [
          {
            type: "heatmap",
            x: content.x,
            y: content.y,
            z: content.magnitude,
            customdata: content.epsilon,
            colorscale: INFERNO,
            colorbar: { title: "|Re(E)|", thickness: 12 },
            hovertemplate: "x = %{x:.3f} µm<br>y = %{y:.3f} µm<br>|Re(E)| = %{z:.4g}<br>ε = %{customdata}<extra></extra>"
          },
          ...contours,
          sources
        ],
        layout: {
          margin: { l: 50, r: 10, t: 10, b: 45 },
          xaxis: { title: "x (µm)" },
          yaxis: { title: "y (µm)", scaleanchor: "x" },
          showlegend: false
        }
      };
    },
    // Vista 3D: superfícies das geometrias e setas do vetor de Poynting (direção da energia)
    viewPlot(content) {
      const surfaces = content.surfaces.map(surface => {
        const info = this.regionInfo(surface.region);
        const epsilon = info.epsilon ? ` · ε = ${info.epsilon}` : "";
        return {
          type: "mesh3d",
          x: surface.x, y: surface.y, z: surface.z,
          i: surface.i, j: surface.j, k: surface.k,
          color: info.color,
          opacity: 0.4,
          name: info.name,
          hovertemplate: `<b>${info.name}</b>${epsilon}<br>(%{x:.2f}, %{y:.2f}, %{z:.2f}) µm<extra></extra>`
        };
      });
      // Setas do mesmo tamanho na direção de S; cor e comprimento seguem log|S| (perto da fonte o fluxo é
      // ordens de grandeza maior). O valor real vai no hover.
      const { x, y, z, u, v, w } = content.arrows;
      const magnitude = u.map((_, i) => Math.hypot(u[i] || 0, v[i] || 0, w[i] || 0));
      const logs = magnitude.map(m => Math.log10(m || 1e-30));
      const low = Math.min(...logs), high = Math.max(...logs);
      const level = logs.map(l => (high > low ? (l - low) / (high - low) : 1));
      const scale = magnitude.map((m, i) => (m ? (0.35 + 0.65 * level[i]) / m : 0));
      const arrows = {
        type: "cone",
        x, y, z,
        u: u.map((value, i) => value * scale[i]),
        v: v.map((value, i) => value * scale[i]),
        w: w.map((value, i) => value * scale[i]),
        sizemode: "absolute",
        sizeref: 0.35,
        anchor: "center",
        colorscale: "Viridis",
        colorbar: { title: "log |S|<br>(relative)", thickness: 12, tickvals: [], len: 0.6 },
        text: magnitude.map((m, i) =>
          `(${format(x[i], 2)}, ${format(y[i], 2)}, ${format(z[i], 2)}) µm<br>` +
          `|S| = ${m.toExponential(2)}<br>direction (${format(u[i] / (m || 1), 2)}, ${format(v[i] / (m || 1), 2)}, ${format(w[i] / (m || 1), 2)})`),
        hovertemplate: "%{text}<extra>Poynting S</extra>"
      };
      const sources = {
        type: "scatter3d",
        mode: "markers",
        x: content.sources.map(s => s[0]),
        y: content.sources.map(s => s[1]),
        z: content.sources.map(s => s[2]),
        marker: { symbol: "diamond", size: 6, color: "red", line: { color: "#000", width: 1 } },
        text: content.sources.map((s, i) => `Source ${i + 1}<br>(${format(s[0])}, ${format(s[1])}, ${format(s[2])}) µm`),
        hovertemplate: "%{text}<extra></extra>",
        name: "Sources"
      };
      return {
        data: [...surfaces, arrows, sources],
        layout: {
          margin: { l: 0, r: 0, t: 10, b: 0 },
          showlegend: false,
          scene: {
            aspectmode: "data",
            xaxis: { title: "x (µm)" },
            yaxis: { title: "y (µm)" },
            zaxis: { title: "z (µm)" },
            // y para cima, como no editor 3D (cilindros, cones e pirâmides ficam em pé ao longo de y)
            camera: { up: { x: 0, y: 1, z: 0 }, eye: { x: 1.3, y: 0.9, z: 1.7 } }
          }
        }
      };
    },
    async load() {
      const { simulationID, productionID } = this.$route.params;
      const base = `/simulate/${simulationID}/${productionID}/${this.simulationType}`;
      try {
        const { data } = await ngsolve.get(`${base}-data`);
        this.plot = this.simulationType === "3d" ? this.viewPlot(data) : this.cutPlot(data);
        if (this.simulationType !== "3d") {
          this.subtitle = `Cut at z = ${format(data.plane_z, 2)} µm · λ = ${data.wavelength} µm`;
        }
        return;
      } catch (err) {
        // Sem os dados JSON (simulação anterior a eles): mostra a imagem
      }
      try {
        const response = await ngsolve.get(base, { responseType: "blob" });
        this.imageUrl = URL.createObjectURL(new Blob([response.data]));
      } catch (err) {
        this.error = `Could not load this result (${err.message}). It may have been deleted.`;
      }
    }
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
  }
};
</script>
<style>
.simulation-image {
  background: #fff;
  border-radius: 4px;
}
.interactive-plot {
  width: 100%;
  height: 460px;
  background: #fff;
  border-radius: 4px;
}
</style>
